import { useMemo } from 'react';
import { useData } from "@hooks/useData";
import { ID } from "@datassential/platform-ui-lib";
import { components } from "@apiSchema";
import { ISocialPost, SocialPosts } from "@models/SocialPosts";
import http from "@core/fetch";
import apiConfig from "@apiConfig";
import dayjs from "dayjs";
import { v4 as uuid } from "uuid";

type Response = components['schemas']['SocialApiKeywordPostData'][];
type PostItem = components['schemas']['SocialApiKeywordPostItem'];

export type ChainMonthlyPostsMap = Record<string, ISocialPost[]>;

const REQUEST_TYPE: keyof typeof apiConfig = 'get posts by keyword ids';
const KEYWORD_TYPE_CHAIN_PROFILE = 3;

function mapPost(post: PostItem): ISocialPost {
    return {
        id: uuid(),
        source: SocialPosts.getPostSource(post.type ?? ''),
        sentiment: post.sentiment ?? 'neutral',
        text: post.content ?? '',
        date: post.date ?? '',
        engagement: post.engagementCount ?? 0,
        url: post.url ?? '',
    };
}

function createFetchFn() {
    let generation = 0;

    return async function fetchChainMonthlyPosts(ids: ID[], startDate: string | null): Promise<ChainMonthlyPostsMap> {
        if (ids.length === 0 || startDate == null) return {};

        const thisGeneration = ++generation;

        const start = dayjs(startDate).startOf('month').format('YYYY-MM-DD');
        const end = dayjs(startDate).endOf('month').format('YYYY-MM-DD');

        try {
            const response: Response = await http.get(REQUEST_TYPE, {
                ids: ids.map(id => String(id)),
                keywordTypeId: KEYWORD_TYPE_CHAIN_PROFILE,
                startDate: start,
                endDate: end,
                isSocial: false,
                isHistorical: true,
            });

            if (thisGeneration !== generation) return new Promise<ChainMonthlyPostsMap>(() => {});

            const result: ChainMonthlyPostsMap = {};
            (response ?? []).forEach(item => {
                if (item.code == null) return;
                const codeNum = Number(item.code);
                if (!Number.isFinite(codeNum)) return;
                result[String(codeNum)] = (item.posts ?? []).map(mapPost);
            });
            return result;
        } catch (err) {
            console.error('Fetch error:', REQUEST_TYPE, err);
            return {};
        }
    };
}

export function useChainMonthlyPosts(ids: ID[], startDate: string | null) {
    const fetchFn = useMemo(() => createFetchFn(), []);
    return useData<ChainMonthlyPostsMap>({}, fetchFn, ids, startDate);
}
